import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  X,
  Download,
  Eye,
  FileText,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  Upload,
  Users,
  Video,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import VideoCallModal from '@/components/video-call/VideoCallModal';

const MESSAGE_PAGE_SIZE = 50;
const FALLBACK_SYNC_INTERVAL_MS = 5000;
const ROOM_FILE_SIZE_LIMIT = 8 * 1024 * 1024;

const sortMessages = (items) => (
  [...items].sort((left, right) => {
    const leftTime = new Date(left.created_at || 0).getTime();
    const rightTime = new Date(right.created_at || 0).getTime();
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    return String(left.message_id || '').localeCompare(String(right.message_id || ''));
  })
);

const mergeMessageLists = (currentMessages, incomingMessages) => {
  const byId = new Map();

  [...currentMessages, ...incomingMessages].forEach((message) => {
    if (!message?.message_id) return;
    byId.set(message.message_id, {
      ...(byId.get(message.message_id) || {}),
      ...message,
    });
  });

  return sortMessages(Array.from(byId.values()));
};

const sortFiles = (items) => (
  [...items].sort((left, right) => {
    const leftTime = new Date(left.uploaded_at || 0).getTime();
    const rightTime = new Date(right.uploaded_at || 0).getTime();
    return rightTime - leftTime;
  })
);

const mergeFileLists = (currentFiles, incomingFiles) => {
  const byId = new Map();

  [...currentFiles, ...incomingFiles].forEach((file) => {
    if (!file?.file_id) return;
    byId.set(file.file_id, {
      ...(byId.get(file.file_id) || {}),
      ...file,
    });
  });

  return sortFiles(Array.from(byId.values()));
};

const formatMessageTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatActivityTime = (value) => {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No activity yet';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (sizeInBytes) => {
  if (!sizeInBytes) return '0 B';
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

const isTextPreviewable = (contentType = '') => (
  contentType.startsWith('text/') ||
  contentType === 'application/json'
);

const connectionMeta = (state) => {
  if (state === 'live') {
    return {
      label: 'Live',
      tone: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      Icon: Wifi,
    };
  }

  if (state === 'connecting' || state === 'reconnecting') {
    return {
      label: state === 'connecting' ? 'Connecting' : 'Reconnecting',
      tone: 'bg-amber-50 text-amber-700 border-amber-100',
      Icon: Loader2,
    };
  }

  if (state === 'fallback') {
    return {
      label: 'Sync mode',
      tone: 'bg-blue-50 text-blue-700 border-blue-100',
      Icon: RefreshCw,
    };
  }

  return {
    label: 'Offline',
    tone: 'bg-rose-50 text-rose-700 border-rose-100',
    Icon: WifiOff,
  };
};

const ResearchProjectRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { api, token, user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [fileSearch, setFileSearch] = useState('');
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activePreviewFileId, setActivePreviewFileId] = useState(null);
  const [activeDownloadFileId, setActiveDownloadFileId] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [connectionState, setConnectionState] = useState('connecting');
  const [startingCall, setStartingCall] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const reconnectAttemptRef = useRef(0);
  const skipNextAutoScrollRef = useRef(false);

  const updateRoomFromMessage = useCallback((message) => {
    if (!message?.created_at) return;

    setRoom((previousRoom) => {
      if (!previousRoom) return previousRoom;
      return {
        ...previousRoom,
        last_activity_at: message.created_at,
        last_message_at: message.created_at,
        last_message_preview: message.message || previousRoom.last_message_preview,
      };
    });
  }, []);

  const mergeMessages = useCallback((incomingMessages, options = {}) => {
    const { replace = false } = options;

    setMessages((previousMessages) => (
      replace
        ? sortMessages(incomingMessages)
        : mergeMessageLists(previousMessages, incomingMessages)
    ));
  }, []);

  const mergeFiles = useCallback((incomingFiles, options = {}) => {
    const { replace = false } = options;

    setFiles((previousFiles) => (
      replace
        ? sortFiles(incomingFiles)
        : mergeFileLists(previousFiles, incomingFiles)
    ));
  }, []);

  const applyMessagePage = useCallback((payload, options = {}) => {
    mergeMessages(payload.messages || [], options);
    setHasMoreMessages(Boolean(payload.has_more));
    setMessagesCursor(payload.next_before || null);
  }, [mergeMessages]);

  const loadRoom = useCallback(async () => {
    const response = await api.get(`/research-rooms/${roomId}`);
    setRoom(response.data);
    return response.data;
  }, [api, roomId]);

  const loadLatestMessages = useCallback(async (options = {}) => {
    const response = await api.get(`/research-rooms/${roomId}/messages`, {
      params: { limit: MESSAGE_PAGE_SIZE },
    });
    applyMessagePage(response.data, options);
    return response.data;
  }, [api, applyMessagePage, roomId]);

  const loadFiles = useCallback(async (options = {}) => {
    const response = await api.get(`/research-rooms/${roomId}/files`);
    mergeFiles(response.data.files || [], options);
    return response.data;
  }, [api, mergeFiles, roomId]);

  const refreshRoomData = useCallback(async (options = {}) => {
    const { silent = false, replaceMessages = false } = options;

    try {
      if (!silent) setSyncing(true);
      await Promise.all([
        loadRoom(),
        loadLatestMessages({ replace: replaceMessages }),
        loadFiles({ replace: true }),
      ]);
    } finally {
      if (!silent) setSyncing(false);
    }
  }, [loadFiles, loadLatestMessages, loadRoom]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadRoom(),
        loadLatestMessages({ replace: true }),
        loadFiles({ replace: true }),
      ]);
    } catch (error) {
      console.error('Failed to load research room:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load room',
        variant: 'destructive',
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [loadFiles, loadLatestMessages, loadRoom, navigate, toast]);

  const loadOlderMessages = useCallback(async () => {
    if (!messagesCursor || loadingMore) return;

    try {
      setLoadingMore(true);
      skipNextAutoScrollRef.current = true;
      const response = await api.get(`/research-rooms/${roomId}/messages`, {
        params: {
          limit: MESSAGE_PAGE_SIZE,
          before: messagesCursor,
        },
      });
      applyMessagePage(response.data, { replace: false });
    } catch (error) {
      console.error('Failed to load older messages:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load older messages',
        variant: 'destructive',
      });
    } finally {
      setLoadingMore(false);
    }
  }, [api, applyMessagePage, loadingMore, messagesCursor, roomId, toast]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (loading) return undefined;
    if (connectionState === 'live') return undefined;

    const intervalId = window.setInterval(() => {
      refreshRoomData({ silent: true, replaceMessages: false }).catch((error) => {
        console.warn('Fallback room sync failed:', error);
      });
    }, FALLBACK_SYNC_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [connectionState, loading, refreshRoomData]);

  useEffect(() => {
    if (!backendUrl || !token || !roomId) return undefined;

    shouldReconnectRef.current = true;
    reconnectAttemptRef.current = 0;

    const connect = () => {
      const websocketUrl = `${backendUrl.replace(/^http/, 'ws')}/api/research-rooms/${roomId}/ws?token=${encodeURIComponent(token)}`;
      const socket = new WebSocket(websocketUrl);
      wsRef.current = socket;
      setConnectionState(reconnectAttemptRef.current > 0 ? 'reconnecting' : 'connecting');

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnectionState('live');
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const payload = data.payload || {};

          if (data.type === 'connection.ready') {
            if (payload.room) {
              setRoom(payload.room);
            }
            refreshRoomData({ silent: true, replaceMessages: false }).catch((error) => {
              console.warn('Socket ready sync failed:', error);
            });
            return;
          }

          if (data.type === 'message.created') {
            if (payload.message) {
              mergeMessages([payload.message]);
              updateRoomFromMessage(payload.message);
            }
            return;
          }

          if (data.type === 'participant.joined') {
            if (payload.room) {
              setRoom(payload.room);
            } else if (payload.participant) {
              setRoom((previousRoom) => {
                if (!previousRoom) return previousRoom;
                if ((previousRoom.participants || []).some((participant) => participant.user_id === payload.participant.user_id)) {
                  return previousRoom;
                }
                return {
                  ...previousRoom,
                  participants: [...(previousRoom.participants || []), payload.participant],
                  participant_count: (previousRoom.participant_count || previousRoom.participants?.length || 0) + 1,
                };
              });
            }

            if (payload.system_message) {
              mergeMessages([payload.system_message]);
              updateRoomFromMessage(payload.system_message);
            }
            return;
          }

          if (data.type === 'file.created') {
            if (payload.file) {
              mergeFiles([payload.file]);
            }
            if (payload.message) {
              mergeMessages([payload.message]);
              updateRoomFromMessage(payload.message);
            }
            return;
          }

          if (data.type === 'CALL_STARTED' || data.type === 'USER_JOINED_CALL' || data.type === 'USER_LEFT_CALL') {
            if (payload.room) {
              setRoom(payload.room);
            } else if (payload.call) {
              setRoom((previousRoom) => (
                previousRoom
                  ? {
                    ...previousRoom,
                    active_call: payload.call.status === 'active' ? payload.call : null,
                  }
                  : previousRoom
              ));
            }

            if (payload.call) {
              setSelectedCall((previousCall) => (
                previousCall?.call_id === payload.call.call_id ? payload.call : previousCall
              ));
            }

            if (payload.message) {
              mergeMessages([payload.message]);
              updateRoomFromMessage(payload.message);
            }
            return;
          }

          if (data.type === 'error') {
            console.warn('Research room websocket error payload:', payload);
          }
        } catch (error) {
          console.error('Failed to parse room websocket event:', error);
        }
      };

      socket.onerror = () => {
        setConnectionState('fallback');
      };

      socket.onclose = () => {
        if (wsRef.current === socket) {
          wsRef.current = null;
        }

        if (!shouldReconnectRef.current) {
          setConnectionState('offline');
          return;
        }

        reconnectAttemptRef.current += 1;
        setConnectionState('fallback');
        const delay = Math.min(10000, reconnectAttemptRef.current * 1500);
        reconnectTimeoutRef.current = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnectionState('offline');
    };
  }, [backendUrl, mergeFiles, mergeMessages, refreshRoomData, roomId, token, updateRoomFromMessage]);

  useEffect(() => {
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }

    messagesEndRef.current?.scrollIntoView({
      behavior: loading ? 'auto' : 'smooth',
    });
  }, [loading, messages]);

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => {
    setSelectedCall((previousCall) => {
      if (!previousCall) return previousCall;
      if (room?.active_call?.call_id === previousCall.call_id) {
        return room.active_call;
      }
      if (!room?.active_call && previousCall.status === 'active') {
        return {
          ...previousCall,
          status: 'ended',
        };
      }
      return previousCall;
    });
  }, [room?.active_call]);

  const sendMessage = async () => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage || sending) return;

    const previousValue = messageText;
    const clientMessageId = `client_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    try {
      setSending(true);
      setMessageText('');

      const response = await api.post(`/research-rooms/${roomId}/messages`, {
        message: trimmedMessage,
        client_message_id: clientMessageId,
      });

      mergeMessages([response.data]);
      updateRoomFromMessage(response.data);
    } catch (error) {
      console.error('Failed to send room message:', error);
      setMessageText(previousValue);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleFileSelected = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';

    if (!selectedFile || uploadingFile) return;
    if (selectedFile.size > ROOM_FILE_SIZE_LIMIT) {
      toast({
        title: 'File too large',
        description: 'Files must be 8 MB or smaller',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploadingFile(true);
      const fileUrl = await fileToDataUrl(selectedFile);
      const response = await api.post(`/research-rooms/${roomId}/files`, {
        filename: selectedFile.name,
        file_url: fileUrl,
        file_size: selectedFile.size,
        content_type: selectedFile.type || null,
      });

      if (response.data.file) {
        mergeFiles([response.data.file]);
      }
      if (response.data.message) {
        mergeMessages([response.data.message]);
        updateRoomFromMessage(response.data.message);
      }

      toast({
        title: 'File shared',
        description: `${selectedFile.name} is now available in the room`,
      });
    } catch (error) {
      console.error('Failed to upload room file:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to share file',
        variant: 'destructive',
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl('');
    setPreviewText('');
    setPreviewLoading(false);
  };

  const openSharedFile = async (file) => {
    try {
      setActivePreviewFileId(file.file_id);
      setPreviewLoading(true);
      setPreviewFile(file);
      setPreviewText('');
      const response = await api.get(`/research-rooms/${roomId}/files/${file.file_id}/content`, {
        responseType: 'blob',
      });

      const blobUrl = URL.createObjectURL(response.data);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(blobUrl);

      if (isTextPreviewable(file.content_type || response.data.type)) {
        const textContent = await response.data.text();
        setPreviewText(textContent);
      }
    } catch (error) {
      console.error('Failed to open room file:', error);
      closePreview();
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to open file',
        variant: 'destructive',
      });
    } finally {
      setPreviewLoading(false);
      setActivePreviewFileId(null);
    }
  };

  const downloadSharedFile = async (file) => {
    try {
      setActiveDownloadFileId(file.file_id);
      const response = await api.get(`/research-rooms/${roomId}/files/${file.file_id}/content`, {
        params: { download: true },
        responseType: 'blob',
      });

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download room file:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to download file',
        variant: 'destructive',
      });
    } finally {
      setActiveDownloadFileId(null);
    }
  };

  const closeCallModal = useCallback(() => {
    setCallModalOpen(false);
    setSelectedCall(null);
  }, []);

  const openCallModal = useCallback((call) => {
    if (!call?.call_id) return;
    setSelectedCall(call);
    setCallModalOpen(true);
  }, []);

  const handleStartCall = async () => {
    if (startingCall) return;

    if (room?.active_call?.status === 'active') {
      openCallModal(room.active_call);
      return;
    }

    try {
      setStartingCall(true);
      const response = await api.post('/start-call', { room_id: roomId });

      if (response.data.room) {
        setRoom(response.data.room);
      }
      if (response.data.message) {
        mergeMessages([response.data.message]);
        updateRoomFromMessage(response.data.message);
      }
      if (response.data.call) {
        openCallModal(response.data.call);
      }
    } catch (error) {
      console.error('Failed to start room call:', error);
      toast({
        title: 'Unable to start call',
        description: error.response?.data?.detail || 'Failed to create the room call',
        variant: 'destructive',
      });
    } finally {
      setStartingCall(false);
    }
  };

  const handleJoinCall = (message) => {
    if (!message?.call_id) return;

    if (room?.active_call?.call_id === message.call_id) {
      openCallModal(room.active_call);
      return;
    }

    openCallModal({
      call_id: message.call_id,
      status: message.call_status || 'active',
      participant_count: message.participant_count || 0,
      started_by_name: message.call_started_by_name || message.user_name,
    });
  };

  const filteredFiles = files.filter((file) =>
    file.filename.toLowerCase().includes(fileSearch.trim().toLowerCase())
  );

  const status = connectionMeta(connectionState);
  const StatusIcon = status.Icon;
  const activeCall = room?.active_call || null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1a5c5c]" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-rose-100 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-rose-500" />
          <h1 className="text-xl font-semibold text-slate-900">Room unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">
            This research room could not be loaded or you do not have access to it.
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-[#1a5c5c] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#154a4a]"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#eef5f5_100%)]">
      <div className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold text-slate-900">{room.title}</h1>
              <p className="truncate text-sm text-slate-600">
                Mentor: {room.mentor_name} • Last activity {formatActivityTime(room.last_activity_at)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${status.tone}`}>
              <StatusIcon className={`h-3.5 w-3.5 ${connectionState === 'connecting' || connectionState === 'reconnecting' ? 'animate-spin' : ''}`} />
              {status.label}
            </span>
            <button
              type="button"
              onClick={() => refreshRoomData({ replaceMessages: false })}
              disabled={syncing}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {connectionState === 'fallback' && (
        <div className="border-b border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700 md:px-8">
          Realtime is unavailable. The room is automatically syncing every 5 seconds until the live connection returns.
        </div>
      )}

      <div className="mx-auto grid min-h-0 max-w-7xl grid-cols-1 gap-6 px-4 py-6 md:px-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="flex h-[calc(100vh-11rem)] max-h-[860px] min-h-[620px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1a5c5c]/10 text-[#1a5c5c]">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Project Discussion</h2>
                <p className="text-sm text-slate-500">
                  {messages.length} messages in this room
                </p>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-5">
            {hasMoreMessages && (
              <div className="mb-4 flex justify-center">
                <button
                  type="button"
                  onClick={loadOlderMessages}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-60"
                >
                  {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Load earlier messages
                </button>
              </div>
            )}

            {messages.length === 0 ? (
              <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 text-center">
                <MessageCircle className="mb-3 h-10 w-10 text-slate-300" />
                <h3 className="text-base font-semibold text-slate-700">No messages yet</h3>
                <p className="mt-1 max-w-sm text-sm text-slate-500">
                  Start the room conversation. Messages are saved on the server and will continue syncing even if realtime drops.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {messages.map((message) => {
                  const isSystem = message.message_type === 'system';
                  const isCallInvite = message.message_type === 'call_invite';
                  const isOwn = message.user_id === user?.user_id;
                  const matchingActiveCall = activeCall?.call_id === message.call_id ? activeCall : null;
                  const callIsLive = Boolean(matchingActiveCall) || message.call_status === 'active';
                  const callIsJoinable = Boolean(message.call_id) && (Boolean(matchingActiveCall) || message.call_status !== 'ended');

                  if (isSystem) {
                    return (
                      <div key={message.message_id} className="flex justify-center">
                        <div className="max-w-xl rounded-full bg-slate-100 px-4 py-2 text-center text-xs font-medium text-slate-600">
                          {message.message}
                        </div>
                      </div>
                    );
                  }

                  if (isCallInvite) {
                    return (
                      <div key={message.message_id} className="flex justify-center">
                        <div className="w-full max-w-2xl rounded-[1.75rem] border border-[#1a5c5c]/15 bg-[linear-gradient(135deg,rgba(26,92,92,0.09),rgba(255,255,255,0.95))] p-4 shadow-sm">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1a5c5c]">
                                <Video className="h-3.5 w-3.5" />
                                Room Call
                              </div>
                              <p className="mt-3 text-base font-semibold text-slate-900">{message.message}</p>
                              <p className="mt-1 text-sm text-slate-500">
                                {message.call_started_by_name || message.user_name} • {formatMessageTime(message.created_at)}
                              </p>
                            </div>

                            <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                              callIsLive
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-slate-200 bg-slate-100 text-slate-500'
                            }`}>
                              {callIsLive ? 'Live now' : 'Ended'}
                            </span>
                          </div>

                          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/60 bg-white/80 px-4 py-3">
                            <p className="text-sm text-slate-600">
                              {matchingActiveCall
                                ? `${matchingActiveCall.participant_count || 0} participant${matchingActiveCall.participant_count === 1 ? '' : 's'} in call`
                                : 'Join from here to open the video room window.'}
                            </p>
                            <button
                              type="button"
                              onClick={() => handleJoinCall(message)}
                              disabled={!callIsJoinable}
                              className="inline-flex items-center gap-2 rounded-full bg-[#1a5c5c] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#154a4a] disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                              <Video className="h-4 w-4" />
                              JOIN CALL
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={message.message_id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-3xl px-4 py-3 shadow-sm md:max-w-[75%] ${
                          isOwn
                            ? 'bg-[#1a5c5c] text-white'
                            : 'border border-slate-200 bg-slate-50 text-slate-900'
                        }`}
                      >
                        <div className="mb-1 flex items-center justify-between gap-3">
                          <span className={`text-xs font-semibold ${isOwn ? 'text-white/80' : 'text-slate-500'}`}>
                            {message.user_name}
                          </span>
                          <span className={`text-[11px] ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
                            {formatMessageTime(message.created_at)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm leading-6">
                          {message.message}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-4 md:px-5">
            <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
              <textarea
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendMessage();
                  }
                }}
                rows={3}
                maxLength={2000}
                placeholder="Write an update, ask a question, or share progress..."
                className="w-full resize-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
              />

              <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-400">
                  {messageText.trim().length}/2000 characters
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleStartCall}
                    disabled={startingCall}
                    className="inline-flex items-center gap-2 rounded-full border border-[#1a5c5c]/15 bg-[#1a5c5c]/8 px-4 py-2 text-sm font-semibold text-[#1a5c5c] transition hover:bg-[#1a5c5c]/12 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {startingCall ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                    {activeCall ? 'Join Live Call' : 'Start Call'}
                  </button>
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={sending || !messageText.trim()}
                    className="inline-flex items-center gap-2 rounded-full bg-[#1a5c5c] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#154a4a] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <section className="flex max-h-[calc(100vh-22rem)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Participants</h2>
                <p className="text-sm text-slate-500">
                  {room.participant_count || room.participants?.length || 0} members
                </p>
              </div>
            </div>

            <div className="mt-5 max-h-[220px] space-y-3 overflow-y-auto pr-1">
              {(room.participants || []).map((participant) => (
                <div
                  key={participant.user_id}
                  className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-3"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1a5c5c] text-sm font-semibold text-white">
                    {(participant.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{participant.name}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {participant.role} • Joined {formatActivityTime(participant.joined_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="flex max-h-[calc(100vh-28.5rem)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1a5c5c]/10 text-[#1a5c5c]">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Shared Files</h2>
                  <p className="text-sm text-slate-500">
                    {filteredFiles.length} shown • {files.length} total
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="inline-flex items-center gap-2 rounded-full bg-[#1a5c5c] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#154a4a] disabled:opacity-60"
              >
                {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelected}
              className="hidden"
            />

            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={fileSearch}
                onChange={(event) => setFileSearch(event.target.value)}
                placeholder="Search shared files..."
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-[#1a5c5c]"
              />
            </div>

            <div className="mt-4 max-h-[320px] space-y-3 overflow-y-auto pr-1">
              {filteredFiles.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center">
                  <FileText className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                  <p className="text-sm font-medium text-slate-700">
                    {files.length === 0 ? 'No files shared yet' : 'No files match your search'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {files.length === 0 ? 'Upload a file to make it available to everyone in the room.' : 'Try a different filename or clear the search input.'}
                  </p>
                </div>
              ) : (
                filteredFiles.map((file) => (
                  <div
                    key={file.file_id}
                    className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-slate-600 shadow-sm">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">{file.filename}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          {formatFileSize(file.file_size)} • {file.uploaded_by_name} • {formatActivityTime(file.uploaded_at)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openSharedFile(file)}
                        disabled={activePreviewFileId === file.file_id}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                      >
                        {activePreviewFileId === file.file_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                        {file.is_previewable ? 'View' : 'Open'}
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadSharedFile(file)}
                        disabled={activeDownloadFileId === file.file_id}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1a5c5c] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#154a4a] disabled:opacity-60"
                      >
                        {activeDownloadFileId === file.file_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                        Download
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Description</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                {room.description || 'No room description provided.'}
              </p>
            </div>
          </section>
        </aside>
      </div>

      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold text-slate-900">{previewFile.filename}</h2>
                <p className="text-sm text-slate-500">
                  {formatFileSize(previewFile.file_size)} • {previewFile.uploaded_by_name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadSharedFile(previewFile)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#1a5c5c] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#154a4a]"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={closePreview}
                  className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-slate-50 p-4">
              {previewLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-[#1a5c5c]" />
                </div>
              ) : previewUrl && (previewFile.content_type || '').startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt={previewFile.filename}
                  className="mx-auto max-h-full max-w-full rounded-2xl object-contain shadow-sm"
                />
              ) : previewUrl && (previewFile.content_type || '') === 'application/pdf' ? (
                <iframe
                  title={previewFile.filename}
                  src={previewUrl}
                  className="h-full min-h-[70vh] w-full rounded-2xl border border-slate-200 bg-white"
                />
              ) : previewUrl && isTextPreviewable(previewFile.content_type || '') ? (
                <pre className="min-h-full whitespace-pre-wrap break-words rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-800">
                  {previewText}
                </pre>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <FileText className="mb-4 h-10 w-10 text-slate-300" />
                  <p className="text-base font-medium text-slate-700">Preview unavailable for this file type</p>
                  <p className="mt-1 text-sm text-slate-500">Use download to open this file locally.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <VideoCallModal
        open={callModalOpen}
        room={room}
        call={selectedCall}
        api={api}
        user={user}
        socket={wsRef.current}
        realtimeState={connectionState}
        onClose={closeCallModal}
      />
    </div>
  );
};

export default ResearchProjectRoom;
